import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkline } from "../Sparkline";
import { DataSourceBadge } from "../DataSourceBadge";
import { formatINR, formatPercent, signColor } from "../../lib/format";

const Side = ({ c, accent, align }) => (
  <Link
    to={`/company/${c.ticker}`}
    data-testid={`compare-head-${c.ticker.toLowerCase()}`}
    className={`group flex-1 p-5 transition-colors sm:p-6 ${align === "right" ? "text-right" : ""}`}
  >
    <span className={`inline-flex rounded border px-2 py-0.5 font-mono text-xs font-bold ${accent === "amber" ? "border-amber-500/30 text-amber-300" : "border-sky-500/30 text-sky-300"}`}>
      {c.ticker}
    </span>
    <h3 className="mt-2 text-lg font-semibold text-white transition-colors duration-200 group-hover:text-amber-200 sm:text-xl">{c.name}</h3>
    {c.sector && <p className="mt-1 text-xs text-zinc-500">{c.sector}</p>}
    <div className={`mt-4 flex items-baseline gap-2 ${align === "right" ? "justify-end" : ""}`}>
      <span className="font-mono text-2xl font-bold tabular-nums text-white">{formatINR(c.price)}</span>
      <span className={`font-mono text-xs ${signColor(c.change_percent)}`}>{formatPercent(c.change_percent)}</span>
    </div>
    <div className="mt-3 h-8 w-full max-w-[150px]" style={align === "right" ? { marginLeft: "auto" } : undefined}>
      <Sparkline data={c.sparkline} color={c.change_percent >= 0 ? "#10B981" : "#F43F5E"} height={32} />
    </div>
  </Link>
);

// Head-to-head comparison hero — real identity + price + sparkline for each
// company, centered on a restrained "HEAD TO HEAD" divider.
export const HeadToHead = ({ a, b, dataSource }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
    data-testid="compare-head-to-head"
    className="lv-panel lv-top-edge relative overflow-hidden"
  >
    <div className="flex flex-col divide-y divide-white/[0.07] sm:flex-row sm:divide-x sm:divide-y-0">
      <Side c={a} accent="amber" />
      <div className="flex items-center justify-center bg-[#0B0C0E] px-8 py-3 sm:py-0">
        <div className="flex flex-col items-center gap-1">
          <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">Head to</span>
          <span className="font-mono text-lg font-bold tracking-widest text-amber-400">VS</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">Head</span>
        </div>
      </div>
      <Side c={b} accent="sky" align="right" />
    </div>
    {dataSource && (
      <div className="flex items-center justify-between gap-3 border-t border-white/[0.06] px-5 py-2.5">
        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Data Context</span>
        <DataSourceBadge metadata={dataSource} />
      </div>
    )}
  </motion.div>
);
